import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { FileText, Plus, Edit2, Trash2, Search, X, Save } from 'lucide-react';

const CATEGORY_LABELS = {
  sheet: 'Sheet', tile: 'Tiles', ridge: 'Ridge', valley: 'Valley',
  barge: 'Barge', spouting: 'Spouting', fastening: 'Fastening', flashing: 'Flashing',
  cleaning: 'Cleaning', recoat: 'Recoat', aquaseal: 'Aquaseal/Dektite',
  safety: 'Safety', travel: 'Travel', urgent: 'Urgent', misc: 'Miscellaneous', underlay: 'Underlay',
};

export default function DescriptionEditorPage() {
  const { api } = useAuth();
  const [groups, setGroups] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [descriptions, setDescriptions] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const loadGroups = useCallback(async () => {
    try {
      const { data } = await api.get('/item-descriptions/by-category');
      setGroups(data);
      if (!activeCategory && data.length > 0) setActiveCategory(data[0].category);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, activeCategory]);

  const loadDescriptions = useCallback(async () => {
    if (!activeCategory) return;
    try {
      const { data } = await api.get(`/item-descriptions?category=${activeCategory}&search=${search}`);
      setDescriptions(data);
    } catch (e) { console.error(e); }
  }, [api, activeCategory, search]);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { loadDescriptions(); }, [loadDescriptions]);

  const addDescription = async () => {
    if (!newDesc.trim() || !activeCategory) return;
    try {
      await api.post('/item-descriptions', { category: activeCategory, description: newDesc.trim() });
      setNewDesc('');
      loadDescriptions();
      loadGroups();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to add'); }
  };

  const updateDescription = async (id) => {
    if (!editText.trim()) return;
    try {
      await api.put(`/item-descriptions/${id}`, { category: activeCategory, description: editText.trim() });
      setEditId(null);
      loadDescriptions();
    } catch (e) { alert('Failed to update'); }
  };

  const deleteDescription = async (id) => {
    if (!window.confirm('Delete this description?')) return;
    await api.delete(`/item-descriptions/${id}`);
    loadDescriptions();
    loadGroups();
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading...</div>;

  const activeGroup = groups.find(g => g.category === activeCategory);

  return (
    <div data-testid="description-editor-page" className="space-y-5">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Item Descriptions</h1>
        <p className="text-sm text-slate-500 mt-1">Manage standard descriptions for each roof component category. These appear as dropdowns in the Quote Builder.</p>
      </div>

      <div className="flex gap-5">
        {/* Category Sidebar */}
        <div className="w-56 flex-shrink-0">
          <div className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden">
            {groups.map(g => (
              <button
                key={g.category}
                data-testid={`desc-category-${g.category}`}
                onClick={() => { setActiveCategory(g.category); setSearch(''); }}
                className={`w-full text-left px-4 py-2.5 text-sm border-b border-[#E2E2DF] last:border-0 transition-colors flex items-center justify-between ${activeCategory === g.category ? 'bg-[#D35400]/5 text-[#D35400] font-semibold' : 'text-slate-700 hover:bg-slate-50'}`}
              >
                <span>{CATEGORY_LABELS[g.category] || g.category}</span>
                <span className="text-xs text-slate-400 font-normal">{g.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Descriptions List */}
        <div className="flex-1 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="desc-search" value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${CATEGORY_LABELS[activeCategory] || activeCategory} descriptions...`} className="w-full pl-9 pr-3 py-2 border border-[#E2E2DF] rounded-lg text-sm" />
            </div>
          </div>

          {/* Add New */}
          <div className="flex gap-2">
            <input
              data-testid="new-desc-input"
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addDescription()}
              placeholder={`Add new ${CATEGORY_LABELS[activeCategory] || activeCategory} description...`}
              className="flex-1 px-3 py-2 border border-[#E2E2DF] rounded-lg text-sm"
            />
            <button data-testid="add-desc-btn" onClick={addDescription} disabled={!newDesc.trim()} className="flex items-center gap-1.5 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000] disabled:opacity-50">
              <Plus size={14} /> Add
            </button>
          </div>

          {/* List */}
          <div className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-[#E2E2DF]">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{CATEGORY_LABELS[activeCategory] || activeCategory} — {descriptions.length} descriptions</span>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {descriptions.map(d => (
                <div key={d._id} className="flex items-center gap-2 px-4 py-2 border-b border-[#E2E2DF] last:border-0 hover:bg-slate-50 group">
                  {editId === d._id ? (
                    <>
                      <input value={editText} onChange={e => setEditText(e.target.value)} onKeyDown={e => e.key === 'Enter' && updateDescription(d._id)} className="flex-1 px-2 py-1 border border-[#D35400] rounded text-sm" autoFocus />
                      <button onClick={() => updateDescription(d._id)} className="p-1 text-green-600 hover:text-green-700"><Save size={14} /></button>
                      <button onClick={() => setEditId(null)} className="p-1 text-slate-400 hover:text-slate-600"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <FileText size={13} className="text-slate-300 flex-shrink-0" />
                      <span className="flex-1 text-sm text-slate-700">{d.description}</span>
                      <button data-testid={`edit-desc-${d._id}`} onClick={() => { setEditId(d._id); setEditText(d.description); }} className="p-1 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-[#D35400]"><Edit2 size={13} /></button>
                      <button data-testid={`delete-desc-${d._id}`} onClick={() => deleteDescription(d._id)} className="p-1 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-500"><Trash2 size={13} /></button>
                    </>
                  )}
                </div>
              ))}
              {descriptions.length === 0 && <p className="px-4 py-6 text-center text-sm text-slate-400">No descriptions found</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
